/**
 * ScoreBreakdown — clickable score history table tied to report_id navigation.
 * Uses scoped /api/credit-reports/history rows and the same selectReport flow as ReportHistorySelector.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import { useReportData } from '../../context/ReportDataContext';
import { useI18n } from '../../context/I18nContext';
import { normalizeScoreHistoryRows } from '../../utils/scoreHistoryRows';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const BUREAUS = [
  { key: 'equifax', short: 'EQ', labelKey: 'dashboard.equifax' },
  { key: 'experian', short: 'EX', labelKey: 'dashboard.experian' },
  { key: 'transunion', short: 'TU', labelKey: 'dashboard.transunion' },
];

const CR = 'creditReport';

const scoreRowKey = (row, idx) =>
  row.report_id || `${row.display_import_date || row.date || 'nodate'}-${idx}`;

const formatCompactDate = (dateStr) => {
  if (!dateStr) return '—';
  const parsed = new Date(dateStr);
  if (!Number.isNaN(parsed.getTime()) && dateStr.includes('T')) {
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  }
  return dateStr;
};

const ScoreBreakdown = ({
  selectedReportId: selectedReportIdProp,
  onReportChange,
  theme,
  className = '',
  titleKey = 'dashboard.scoreBreakdown',
  effectiveUserId: effectiveUserIdProp = null,
  cardWrapper: CardWrapper = null,
}) => {
  const { t } = useI18n();
  const { user, getAuthHeaders } = useAuth();
  const { isInSupportMode, supportContext } = useSupportContext();
  const {
    selectedReportId: contextSelectedReportId,
    reportData,
    selectReport,
    isReportLocked,
  } = useReportData();

  const [fetchedRows, setFetchedRows] = useState([]);
  const [scopeUserId, setScopeUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState(null);

  const effectiveUserId =
    effectiveUserIdProp
    || scopeUserId
    || reportData?.user_id
    || (isInSupportMode ? supportContext?.effective_user_id : user?.user_id)
    || null;

  const activeReportId =
    selectedReportIdProp || contextSelectedReportId || reportData?.report_id || null;

  const fetchScoreHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/credit-reports/history`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const apiScopeUserId = response.data?.scope_user_id || response.data?.user_id || null;
      setScopeUserId(apiScopeUserId);

      const reports = (response.data?.reports || []).filter(
        (report) => report.report_id && BUREAUS.some(({ key }) => report[key] != null),
      );
      setFetchedRows(reports);
    } catch (err) {
      console.error('[ScoreBreakdown] Failed to fetch scoped report history:', err);
      setFetchedRows([]);
      setScopeUserId(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchScoreHistory();
  }, [fetchScoreHistory, activeReportId, reportData?.report_id, effectiveUserIdProp]);

  const rows = useMemo(
    () => normalizeScoreHistoryRows(fetchedRows, effectiveUserId),
    [fetchedRows, effectiveUserId],
  );

  const isDark = theme?.isDark ?? false;
  const cardBorder = theme?.card?.border || theme?.border?.b2 || 'rgba(15,172,108,0.12)';
  const cardBg = theme?.card?.bg || theme?.card?.fill || (isDark ? 'rgba(20,40,32,0.95)' : 'rgba(255,255,255,0.95)');
  const rowSurface = theme?.table?.row || (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)');
  const rowHover = theme?.table?.rowHover || (isDark ? 'rgba(142,218,182,0.08)' : 'rgba(15,172,108,0.06)');
  const rowSelected = isDark ? 'rgba(15,172,108,0.16)' : 'rgba(15,172,108,0.12)';
  const accentColor = theme?.green?.[700] || theme?.accent || '#0B8C58';

  const handleRowSelect = async (row) => {
    const reportId = row?.report_id;
    if (!reportId || reportId === activeReportId || isReportLocked) return;

    setSelectingId(reportId);
    try {
      await selectReport(reportId);
      onReportChange?.(reportId);
    } catch (err) {
      console.error('[ScoreBreakdown] Failed to select report:', err);
    } finally {
      setSelectingId(null);
    }
  };

  const renderDateCell = (row, { compact = false } = {}) => {
    const importDate = row.display_import_date || row.date || '—';
    const importLabel = compact ? formatCompactDate(importDate) : importDate;

    return (
      <div className="min-w-0">
        <span
          className={`block font-medium tabular-nums truncate ${compact ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm'}`}
          style={{ color: theme?.text?.secondary || theme?.text?.primary }}
        >
          {t(`${CR}.reportHistoryImportedLabel`, { date: importLabel })}
        </span>
        {row.display_report_date ? (
          <span
            className="block text-[10px] sm:text-xs mt-0.5 truncate"
            style={{ color: theme?.text?.muted }}
          >
            {t(`${CR}.reportHistoryProviderReportDate`, { date: row.display_report_date })}
          </span>
        ) : null}
      </div>
    );
  };

  const renderRowContent = (row, idx) => {
    const isSelected = row.report_id && row.report_id === activeReportId;
    const isClickable = Boolean(row.report_id) && !isReportLocked;
    const isSelecting = selectingId === row.report_id;

    const sharedProps = {
      key: scoreRowKey(row, idx),
      'data-testid': `score-breakdown-row-${row.report_id}`,
      className: `transition-colors ${isClickable ? 'cursor-pointer' : 'cursor-default'}`,
      style: {
        background: isSelected ? rowSelected : 'transparent',
        opacity: isSelecting ? 0.7 : 1,
      },
      ...(isClickable
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => handleRowSelect(row),
            onKeyDown: (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleRowSelect(row);
              }
            },
          }
        : {}),
    };

    const hoverHandlers = isClickable
      ? {
          onMouseEnter: (event) => {
            if (!isSelected) event.currentTarget.style.background = rowHover;
          },
          onMouseLeave: (event) => {
            event.currentTarget.style.background = isSelected ? rowSelected : 'transparent';
          },
        }
      : {};

    return { isSelected, sharedProps, hoverHandlers, rowSurface };
  };

  const cardContent = (
    <>
      <div className="p-3 sm:p-4 border-b" style={{ borderColor: cardBorder }}>
        <h3 className="font-semibold text-sm sm:text-base" style={{ color: theme?.text?.primary }}>
          {t(titleKey)}
        </h3>
      </div>

      {loading ? (
        <div className="p-6 sm:p-8 text-center">
          <p className="text-sm" style={{ color: theme?.text?.muted }}>{t('common.loading')}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="p-6 sm:p-8 text-center">
          <p className="text-sm" style={{ color: theme?.text?.muted }}>{t('dashboard.noScoreHistory')}</p>
        </div>
      ) : (
        <>
          <div className="md:hidden max-h-[min(420px,55vh)] overflow-y-auto overscroll-contain">
            <div
              className="grid grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] gap-1 px-3 py-2 border-b text-[10px] font-semibold uppercase tracking-wide"
              style={{ borderColor: cardBorder, color: theme?.text?.muted }}
              aria-hidden="true"
            >
              <span>{t('common.date')}</span>
              {BUREAUS.map((b) => (
                <span key={b.key} className="text-center" title={t(b.labelKey)}>{b.short}</span>
              ))}
            </div>
            <div className="divide-y" style={{ borderColor: cardBorder }}>
              {rows.map((row, idx) => {
                const { sharedProps, hoverHandlers, rowSurface: surface } = renderRowContent(row, idx);
                return (
                  <div
                    {...sharedProps}
                    {...hoverHandlers}
                    className={`grid grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] gap-1 items-center px-3 py-2.5 ${sharedProps.className}`}
                  >
                    {renderDateCell(row, { compact: true })}
                    {BUREAUS.map(({ key, labelKey }) => (
                      <div
                        key={key}
                        className="rounded-lg px-1 py-1.5 text-center min-w-0"
                        style={{ background: surface }}
                        title={t(labelKey)}
                      >
                        <span className="sr-only">{t(labelKey)}</span>
                        <span
                          className="text-sm font-semibold tabular-nums leading-none"
                          style={{ color: theme?.text?.primary }}
                        >
                          {row[key] ?? '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden md:block overflow-x-auto max-h-[min(420px,55vh)] overflow-y-auto overscroll-contain">
            <table className="w-full min-w-[32rem] table-fixed">
              <thead>
                <tr style={{ background: theme?.table?.header || rowSurface }}>
                  <th className="text-left px-3 lg:px-4 py-2.5 lg:py-3 text-xs font-medium w-[36%]" style={{ color: theme?.text?.muted }}>
                    {t('common.date')}
                  </th>
                  {BUREAUS.map((b) => (
                    <th
                      key={b.key}
                      className="text-center px-2 lg:px-4 py-2.5 lg:py-3 text-xs font-medium"
                      style={{ color: theme?.text?.muted }}
                    >
                      <span className="hidden lg:inline">{t(b.labelKey)}</span>
                      <span className="lg:hidden" title={t(b.labelKey)}>{b.short}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const { isSelected, sharedProps, hoverHandlers } = renderRowContent(row, idx);
                  return (
                    <tr
                      {...sharedProps}
                      {...hoverHandlers}
                      className={`border-t ${sharedProps.className}`}
                      style={{
                        ...sharedProps.style,
                        borderColor: cardBorder,
                        boxShadow: isSelected ? `inset 3px 0 0 ${accentColor}` : undefined,
                      }}
                    >
                      <td className="px-3 lg:px-4 py-2.5 lg:py-3">
                        {renderDateCell(row)}
                      </td>
                      {BUREAUS.map(({ key }) => (
                        <td
                          key={key}
                          className="px-2 lg:px-4 py-2.5 lg:py-3 text-sm text-center font-medium tabular-nums"
                          style={{ color: theme?.text?.primary }}
                        >
                          {row[key] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );

  if (CardWrapper) {
    return (
      <CardWrapper theme={theme} solid noPadding className={`min-w-0 ${className}`}>
        {cardContent}
      </CardWrapper>
    );
  }

  return (
    <section
      className={`rounded-2xl overflow-hidden min-w-0 ${className}`}
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        boxShadow: theme?.card?.shadow,
      }}
      data-testid="score-breakdown"
    >
      {cardContent}
    </section>
  );
};

export default ScoreBreakdown;
